import * as React from 'react';

import MainLayout from 'components/Layout/MainLayout';
import HeaderSchedulePage from 'shared/Header/HeaderSchedulePage';
import MySEO from 'shared/MySEO';
import styled from 'styled-components';
import { colorsV2 } from 'style/colors-v2';
import { Typography } from 'components/DesignSystemV2/Typography';
import { BreadCrumb } from 'shared/CourseLanding/Detail/BreadCrumb';
import { withHomePageSection } from 'components/withHomePageSection';
import { LazyImage } from 'components/LazyImage';
import { fromScreen } from 'utils/media-query/responsive.util';
import DolRegister from 'shared/DolRegister';
import { FixedTopBarScholarshipScheduleNOffline } from 'shared/SchedulePage/FixedTopBarScholarship/FixedTopBarScholarshipScheduleNOffline';

const Space = styled.div`
  > div,
  section {
    padding: 32px 0;
    ${fromScreen(776)} {
      padding: 80px 0;
      &:first-child {
        padding: 100px 0 80px 0;
      }
      &:last-child {
        padding: 80px 0 100px 0;
      }
    }
  }
`;

export const PageContainer = styled.div`
  .page-banner-container {
    .title {
      margin-bottom: 20px;
      .highlight {
        position: relative;
        z-index: 1;
        &::after {
          content: '';
          position: absolute;
          background: ${colorsV2.yellow100};
          bottom: 3px;
          left: 0;
          height: 10px;
          width: 100%;
          opacity: 0.3;
          z-index: -1;
          bottom: 3px;
        }
      }
    }
    background-color: ${colorsV2.paper};
    margin: 0;
    padding: 32px 0px;
    .breadcrumb {
      margin-bottom: 32px;
      a:last-child > p {
        color: ${colorsV2.black80};
      }
    }
    .page-banner-content {
      text-align: left;
      position: relative;
      display: grid;
      grid-auto-flow: row;
      .course-icon {
        display: none;
      }
    }

    ${fromScreen(776)} {
      padding: 32px 0 100px 0;
      .page-banner-content {
        text-align: center;
        .banner-text {
          width: 500px;
          margin: 0 auto;
        }
        .course-icon {
          display: block;
          position: absolute;
          &.right {
            bottom: 0;
            right: 0;
          }
          &.left {
            bottom: -50px;
            left: 70px;
            transform: rotate(170deg);
          }
          &.top {
            top: 32px;
            right: 150px;
          }
        }
      }
    }
    ${fromScreen(1144)} {
      padding: ${props => (props.courseTop ? '32px 0 310px 0' : '')};
      .breadcrumb {
        margin-bottom: 48px;
      }
      .title {
        margin-bottom: 32px;
      }
      .page-banner-content {
        .banner-text {
          width: 662px;
        }
        .course-icon {
          &.top {
            right: 300px;
          }
        }
      }
    }
  }
`;

export const PageBanner = ({
  breadcrumbLinks,
  title,
  description,
  highlight
}) => {
  const Component = withHomePageSection(
    () => (
      <>
        <BreadCrumb links={breadcrumbLinks} />
        <div className="banner-text">
          <Title>
            {title} <span className="highlight">{highlight}</span>
          </Title>
          <NormalText>{description}</NormalText>
        </div>
        <LazyImage
          handle="RY1uavGWQqW2aZ0GGJDo"
          className="course-icon top"
          alt="icon-star"
        />
        <LazyImage
          handle="pZtoUY3mStuWmaQ5lBx6"
          className="course-icon left"
          alt="icon-arrow"
        />
        <LazyImage
          handle="1KmuTBLmS4uEu98y6Tp3"
          className="course-icon right"
          alt="icon-wavy"
        />
      </>
    ),
    { className: 'page-banner-container' },
    { className: 'page-banner-content' }
  );

  return <Component />;
};

export const PageContent = withHomePageSection(
  styled.div``,
  { className: 'page-content-container' },
  { className: 'page-content-content' }
);

export const Title = ({ children }) => (
  <Typography
    variant="bold/32-40"
    tabletVariant="bold/40-48"
    desktopVariant="bold/48-60"
    as="h1"
    className="title"
    v3
  >
    {children}
  </Typography>
);
export const SubTitle = ({ children }) => (
  <Typography variant="semi-bold/16-24" className="sub-title">
    {children}
  </Typography>
);
export const NormalText = ({ children }) => (
  <Typography
    variant="regular/16-24"
    desktopVariant="regular/20-28"
    color={colorsV2.balck80}
    v3
  >
    {children}
  </Typography>
);
export const SubText = ({ children }) => (
  <Typography
    variant="regular/12-20"
    className="sub-text"
    style={{ color: colorsV2.gray100 }}
  >
    {children}
  </Typography>
);

const Layout = ({
  title,
  ieltsDesc,
  children,
  courseTop,
  highlight,
  seoTitle,
  seoDesc,
  featureImg,
  slug
}) => {
  const courseNameDisplay = `${title} ${highlight}`;
  const pageUrl = process.env.SITE_URL + slug;

  const breadCrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'WebPage',
          '@id': process.env.SITE_URL,
          url: process.env.SITE_URL,
          name: 'Trang chủ'
        }
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': `${process.env.SITE_URL}/khoa-hoc-ielts`,
          url: `${process.env.SITE_URL}/khoa-hoc-ielts`,
          name: 'Khoá học IELTS'
        }
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@id': pageUrl,
          url: pageUrl,
          name: courseNameDisplay
        }
      }
    ]
  };

  return (
    <MainLayout
      noHeader
      hideSideCTA
      renderCustomHeader={() => <HeaderSchedulePage />}
      showFooterRegister={false}
    >
      <MySEO
        title={seoTitle}
        description={seoDesc}
        featureImg={featureImg}
        h1={courseNameDisplay}
        slug={slug}
        breadCrumbSchema={breadCrumbList}
      />
      <FixedTopBarScholarshipScheduleNOffline />

      <PageContainer courseTop={courseTop}>
        <PageBanner
          breadcrumbLinks={[
            {
              text: 'Trang chủ',
              to: '/',
              tabletOnly: true
            },
            {
              text: 'Khoá học IELTS',
              to: '/khoa-hoc-ielts'
            },
            {
              text: courseNameDisplay,
              to: ''
            }
          ]}
          title={title}
          description={ieltsDesc}
          highlight={highlight}
        />
        <PageContent>
          <Space>{children}</Space>
        </PageContent>
        <DolRegister />
      </PageContainer>
    </MainLayout>
  );
};

export default React.memo(Layout);
